import React, { useState } from 'react';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { ClassroomDto, EducatorDto } from '../../../../api-service/clients/auto-generated-clients';
import { CourseBulkEditDialogComponent } from './course-bulk-edit-dialog-component';
import { CourseBulkEditResultDialogComponent } from './course-bulk-edit-result-dialog-component';
import { courseResultSection, IBulkEditResultSection } from './bulk-edit-results';
import { useCompanyEducators } from './use-company-educators';
import { useCompanyClassrooms } from './use-company-classrooms';

export interface IChangeCoursesDialogProps {
  courseIds: string[];
  companyId: string;
  onCompleted?: () => void;
}

const attendanceClient = moduleClientFactory.createAttendanceClient();

export function ChangeCoursesDialog({ courseIds, companyId, onCompleted }: IChangeCoursesDialogProps) {
  const { educators, isLoadingEducators } = useCompanyEducators(companyId);
  const { classrooms, isLoadingClassrooms } = useCompanyClassrooms(companyId);
  const [isOpen, setIsOpen] = useState(true);
  const [educatorIdsToAdd, setEducatorIdsToAdd] = useState<string[]>([]);
  const [educatorIdsToRemove, setEducatorIdsToRemove] = useState<string[]>([]);
  const [classroomId, setClassroomId] = useState<string>();
  const [includeFutureLessons, setIncludeFutureLessons] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [completedSections, setCompletedSections] = useState<IBulkEditResultSection[]>([]);
  const [hasSkippedItems, setHasSkippedItems] = useState(false);

  function handleEducatorsToAddChange(chosen: EducatorDto[]) {
    setEducatorIdsToAdd(chosen.map(educator => educator.educatorId));
  }

  function handleEducatorsToRemoveChange(chosen: EducatorDto[]) {
    setEducatorIdsToRemove(chosen.map(educator => educator.educatorId));
  }

  function handleClassroomChange(classroom?: ClassroomDto) {
    setClassroomId(classroom?.classroomId);
  }

  function handleIncludeFutureLessonsToggle() {
    setIncludeFutureLessons(!includeFutureLessons);
  }

  async function handleConfirm() {
    setIsSaving(true);
    const sections = [...completedSections];
    try {
      if (educatorIdsToAdd.length > 0 || educatorIdsToRemove.length > 0) {
        const response = await attendanceClient.addAndRemoveCourseEducators({ courseIds, educatorIdsToAdd, educatorIdsToRemove, includeFutureLessons });
        sections.push(courseResultSection('educators', response, includeFutureLessons));
        // A rejected classroom change keeps the dialog open, and a retry must not apply the educators twice
        setEducatorIdsToAdd([]);
        setEducatorIdsToRemove([]);
      }
      if (classroomId) {
        const response = await attendanceClient.changeCourseClassroom({ courseIds, classroomId, includeFutureLessons });
        sections.push(courseResultSection('classroom', response, includeFutureLessons));
      }
      if (sections.some(section => section.skippedItems.length > 0)) {
        setHasSkippedItems(true);
      } else {
        handleResultClose();
      }
    } catch {
      // The error message interceptor shows the reason, and the dialog stays open so the choice can be corrected
    } finally {
      setCompletedSections(sections);
      setIsSaving(false);
    }
  }

  function handleClose() {
    setIsOpen(false);
    if (completedSections.length > 0) {
      onCompleted?.();
    }
  }

  function handleResultClose() {
    setIsOpen(false);
    onCompleted?.();
  }

  if (!isOpen) {
    return null;
  }

  if (hasSkippedItems) {
    return <CourseBulkEditResultDialogComponent title="Nogle hold blev ikke rettet" sections={completedSections} onClose={handleResultClose} />;
  }

  return (
    <CourseBulkEditDialogComponent
      title={courseIds.length === 1 ? 'Ret undervisere og lokale på det valgte hold' : `Ret undervisere og lokale på de ${courseIds.length} valgte hold`}
      description="Holdene beholder deres undervisere. De valgte undervisere tilføjes eller fjernes."
      educators={educators}
      educatorIdsToAdd={educatorIdsToAdd}
      educatorIdsToRemove={educatorIdsToRemove}
      classrooms={classrooms}
      classroomId={classroomId}
      futureLessonsOption={{
        label: 'Ret også holdenes fremtidige lektioner',
        description:
          'Kladder kommer med. Kun lektioner med holdets nuværende lokale får det nye lokale. Aflyste lektioner og lektioner med låst protokol ændres ikke.',
        checked: includeFutureLessons,
        onToggle: handleIncludeFutureLessonsToggle,
      }}
      isLoading={isLoadingEducators || isLoadingClassrooms}
      isSaving={isSaving}
      onEducatorsToAddChange={handleEducatorsToAddChange}
      onEducatorsToRemoveChange={handleEducatorsToRemoveChange}
      onClassroomChange={handleClassroomChange}
      onConfirm={handleConfirm}
      onClose={handleClose}
    />
  );
}
